// src/pages/UsersPage.js // menu di spalla
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography, Box, CircularProgress, Alert, Paper, Button,
  TextField, IconButton, InputAdornment, Stack
} from '@mui/material';
import { styled } from "@mui/system";
import UserListTable from '../components/UserListTable';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';

// --- STILI UNIFICATI (Copiati dalla Dashboard per coerenza) ---

const MainContainer = styled(Box)({
  minHeight: "100vh",
  background: "#F8F9FA", // Grigio perla
  padding: "30px",
  fontFamily: "'Inter', sans-serif",
});

const ModernCard = styled(Paper)({
  background: "#FFFFFF",
  borderRadius: "24px",
  boxShadow: "0px 10px 40px rgba(0,0,0,0.04)",
  padding: "24px",
  overflow: "hidden" // Per i bordi della tabella
});

const PageTitle = styled(Typography)({
  fontWeight: 800,
  fontSize: "2rem",
  color: "#2d3436",
  letterSpacing: "-1px"
});

// Search bar customizzata "Apple Style"
const SearchField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
    "& fieldset": { border: "1px solid rgba(0,0,0,0.05)" },
    "&:hover fieldset": { borderColor: "#a32b2b" },
    "&.Mui-focused fieldset": { borderColor: "#a32b2b", borderWidth: "2px" },
  }
});

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const usersCollectionRef = collection(db, "users");
            const usersSnapshot = await getDocs(usersCollectionRef);
            const usersData = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));
            setUsers(usersData);
            setError(null);
        } catch (err) {
            setError(err.message || "Errore nel recupero degli utenti.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const filteredUsers = useMemo(() => {
        if (!searchTerm) return users;
        const lowercasedSearch = searchTerm.toLowerCase();
        return users.filter(user => {
            const nameMatch = (user.fullName || '').toLowerCase().includes(lowercasedSearch);
            const emailMatch = (user.email || '').toLowerCase().includes(lowercasedSearch);
            const phoneMatch = (user.phone || '').toString().includes(lowercasedSearch);
            return nameMatch || emailMatch || phoneMatch;
        });
    }, [users, searchTerm]);

    if (loading && users.length === 0) {
        return (
            <MainContainer sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress sx={{ color: '#a32b2b' }} />
            </MainContainer>
        );
    }

    if (error) {
        return (
            <MainContainer>
                <Alert severity="error" variant="filled" sx={{ borderRadius: 4 }}>Errore: {error}</Alert>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            {/* HEADER & AZIONI */}
            <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                
                <Box>
                    <PageTitle>Lista Iscritti</PageTitle>
                    <Typography variant="body2" color="text.secondary">
                        Gestisci le anagrafiche dei tuoi {users.length} clienti
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', md: 'auto' } }}>
                    {/* Barra Ricerca */}
                    <SearchField
                        placeholder="Cerca nome, email..."
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#b2bec3' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: { xs: '100%', md: '300px' } }}
                    />

                    {/* Bottone Refresh */}
                    <Paper elevation={0} sx={{ borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                        <IconButton onClick={fetchUsers} disabled={loading} sx={{ height: '100%', width: '40px', color: '#636e72' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Paper>

                    {/* Bottone Nuovo - Rosso Corporate */}
                    <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={() => navigate('/users/new')}
                        sx={{
                            borderRadius: '12px',
                            backgroundColor: '#a32b2b', // Colore Sidebar
                            fontWeight: 'bold',
                            boxShadow: '0 4px 14px 0 rgba(163, 43, 43, 0.39)',
                            textTransform: 'none',
                            px: 3,
                            '&:hover': { backgroundColor: '#802222' }
                        }}
                    >
                        Nuovo Utente
                    </Button>
                </Stack>
            </Box>

            {/* TABELLA DENTRO LA CARD */}
            <ModernCard>
                {/* Loader discreto interno */}
                {loading && users.length > 0 && (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#a32b2b' }} />
                    </Box>
                )}
                
                {/* UserListTable ora vive qui dentro. 
                    NOTA: Se UserListTable ha un <Paper> interno nel suo codice, 
                    dovresti rimuoverlo da quel file per evitare "scatola dentro scatola".
                */}
                <UserListTable users={filteredUsers} />
            </ModernCard>

        </MainContainer>
    );
}